import { useState } from 'react';

import { useQueries } from '@tanstack/react-query';
import { CalendarIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { campaignsEndpoints } from '@/api/endpoints';
import { SimpleFilterCombobox } from '@/components/ui/custom/SimpleFilterCombobox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DataTable } from '@/components/ui/table/data-table';
import { useAccount } from '@/hooks/useAccount';
import {
  getMonthOptions,
  useVoiceUsage,
  VOICE_USAGE_PAGE_SIZE,
} from '@/modules/billing/hooks/useVoiceUsage';
import { API } from '@/modules/campaigns/api';
import { formatTotalDuration } from '@/modules/campaigns/components/performance/callDurationLabel';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import { CARRIER_LABELS, DIRECTION_LABEL_KEYS, voiceUsageColumns } from './voiceUsageColumns';

const SUMMARY_SKELETON_ROWS = ['first', 'second', 'third'];
const SUMMARY_CELLS = [
  { id: 'carrier', className: 'pl-6' },
  { id: 'direction', className: '' },
  { id: 'calls', className: 'text-right' },
  { id: 'call-time', className: 'text-right' },
  { id: 'billed-minutes', className: 'pr-6 text-right' },
];

/** `'YYYY-MM'` as "October 2026" in the user's language; months are UTC. */
const formatMonth = (month: string, language: string | undefined) => {
  const [year, monthNumber] = month.split('-').map(Number);
  return new Intl.DateTimeFormat(language, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, monthNumber - 1, 1)));
};

export const VoiceUsage = () => {
  const { t, i18n } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const { account } = useAccount();
  // Opening the conversation log needs analytics access, which the Billing role does not have,
  // and an account that has not opted out of analytics.
  const canReadAnalytics = useSelector((state: RootState) =>
    getPermissions(state, 'analytics', actions.READ)
  );
  const canOpenCall = canReadAnalytics && !account?.opt_out;
  const monthOptions = getMonthOptions(new Date()).map((value) => ({
    value,
    label: formatMonth(value, i18n.language),
  }));
  const [month, setMonth] = useState(monthOptions[0].value);
  const [page, setPage] = useState(0);
  const { summary, calls, totalCalls, isSummaryLoading, isCallsLoading, isError } = useVoiceUsage({
    month,
    page,
  });
  const canReadCampaigns = useSelector((state: RootState) =>
    getPermissions(state, 'campaigns', actions.READ)
  );
  const campaignIds = [...new Set(calls.flatMap((call) => call.campaignId ?? []))];
  // Same query key as useCampaigns, so a campaign already loaded elsewhere is not fetched again.
  const campaignResults = useQueries({
    queries: campaignIds.map((id) => ({
      queryKey: [campaignsEndpoints.campaign(id)],
      queryFn: () => API.getCampaign(id),
      enabled: canReadCampaigns,
    })),
  });
  const isLoadingCampaignNames = campaignResults.some(({ isLoading }) => isLoading);
  const isLoadingTables = isSummaryLoading || isCallsLoading || isLoadingCampaignNames;
  const showSummarySkeleton = isSummaryLoading || (page === 0 && isLoadingTables);
  const campaignNames = Object.fromEntries(
    campaignResults.flatMap(({ data, error }, i) => {
      if (data) {
        return [[data.campaign_id, data.name]];
      }
      if ((error as { statusCode?: number } | null)?.statusCode === 404) {
        return [[campaignIds[i], t('campaigns.deleted_campaign')]];
      }
      return [];
    })
  );

  const handleMonthChange = (value: string) => {
    setMonth(value);
    setPage(0);
  };

  const renderSummary = () => {
    // A month with no calls keeps only the calls table, which shows the empty message.
    if (!showSummarySkeleton && summary.length === 0) {
      return null;
    }
    return (
      <>
        <Table fixedLayout className={showSummarySkeleton ? 'mb-6' : undefined}>
          <TableHeader>
            <TableRow className="h-12">
              <TableHead className="w-3/12 pl-6">{t('invoices.voice_usage.carrier')}</TableHead>
              <TableHead className="w-3/12">{t('invoices.voice_usage.direction')}</TableHead>
              <TableHead className="w-2/12 text-right">{t('invoices.voice_usage.calls')}</TableHead>
              <TableHead className="w-2/12 text-right">
                {t('invoices.voice_usage.call_time')}
              </TableHead>
              <TableHead className="w-2/12 pr-6 text-right">
                {t('invoices.voice_usage.billed_minutes')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {showSummarySkeleton
              ? SUMMARY_SKELETON_ROWS.map((row) => (
                  <TableRow key={row}>
                    {SUMMARY_CELLS.map(({ id, className }) => (
                      <TableCell key={id} className={className}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : summary.map((row) => (
                  <TableRow key={`${row.carrier}-${row.direction}`}>
                    <TableCell className="pl-6">{CARRIER_LABELS[row.carrier]}</TableCell>
                    <TableCell>{t(DIRECTION_LABEL_KEYS[row.direction])}</TableCell>
                    <TableCell className="text-right tabular-nums">{row.calls}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatTotalDuration(row.durationSeconds)}
                    </TableCell>
                    <TableCell className="pr-6 text-right tabular-nums">
                      {row.billableMinutes}
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
        {!showSummarySkeleton && (
          <p className="mt-2 mb-6 text-xs text-muted-foreground">
            {t('invoices.voice_usage.rounding_note')}
          </p>
        )}
      </>
    );
  };

  const renderBody = () => {
    if (isError) {
      return <p className="text-sm text-muted-foreground">{t('invoices.voice_usage.error')}</p>;
    }
    return (
      <>
        {renderSummary()}
        <DataTable.Root
          columns={voiceUsageColumns(campaignNames)}
          data={calls}
          pagination
          currentPageIndex={page}
          setCurrentPageIndex={setPage}
          fetchNextPage={() => setPage(page + 1)}
          hasNextPage={(page + 1) * VOICE_USAGE_PAGE_SIZE < totalCalls}
          rowCount={totalCalls}
          resourceName="call"
          isLoading={isLoadingTables}
          emptyStateMessage={t('invoices.voice_usage.empty')}
          getRowId={(row) => row.callId}
          getRowHref={
            canOpenCall
              ? (row) => `/${slug}/analytics/account-chat-logs?session_id=${row.sessionId}`
              : undefined
          }
        >
          <DataTable.Content />
          <DataTable.Pagination />
        </DataTable.Root>
      </>
    );
  };

  return (
    <section>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-base leading-6 font-semibold text-foreground">
          {t('invoices.voice_usage.title')}
        </h2>
        <SimpleFilterCombobox
          options={monthOptions}
          value={month}
          onChange={handleMonthChange}
          icon={<CalendarIcon />}
        />
      </div>
      {renderBody()}
    </section>
  );
};
